<script lang="ts">
  import { push, router } from 'svelte-spa-router';
  import Header from '../../app/layout/Header.svelte';
  import { listCategories } from '../../data/categories';
  import { getExpense, addExpense, updateExpense } from '../../data/expenses';
  import { getTodayDateString } from '../../lib/date';

  interface Props {
    params: { categoryId: string; expenseId?: string };
  }

  let { params }: Props = $props();

  const isEditMode = $derived(Boolean(params.expenseId));

  let categoryName = $state('カテゴリ');
  let date = $state(getTodayDateString());
  let amountText = $state('');
  let memo = $state('');
  let paymentMethod = $state('');
  let initialDate = $state(getTodayDateString());
  let loading = $state(true);
  let saving = $state(false);
  let errorMessage = $state('');

  function getQueryDate(): string | null {
    return new URLSearchParams(router.querystring ?? '').get('date');
  }

  async function loadData() {
    loading = true;
    errorMessage = '';
    try {
      const categories = await listCategories();
      const category = categories.find((c) => c.id === params.categoryId);
      categoryName = category?.name ?? 'カテゴリ';

      if (params.expenseId) {
        const expense = await getExpense(params.expenseId);
        if (!expense) {
          errorMessage = '支出データが見つかりませんでした。';
          return;
        }
        date = expense.date;
        amountText = String(expense.amount);
        memo = expense.memo;
        paymentMethod = expense.paymentMethod ?? '';
        initialDate = expense.date;
      } else {
        const queryDate = getQueryDate();
        date = queryDate ?? getTodayDateString();
        initialDate = date;
      }
    } catch (err) {
      console.error(err);
      errorMessage = 'データの読み込みに失敗しました。';
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    loadData();
  });

  function backPath(): string {
    return `/category/${params.categoryId}/day/${initialDate}`;
  }

  async function handleSubmit(event: SubmitEvent) {
    event.preventDefault();
    errorMessage = '';

    if (!date) {
      errorMessage = '日付を入力してください。';
      return;
    }
    const amount = Number(amountText);
    if (!amountText || Number.isNaN(amount) || !Number.isInteger(amount) || amount <= 0) {
      errorMessage = '金額は1円以上の整数で入力してください。';
      return;
    }

    saving = true;
    try {
      const trimmedPaymentMethod = paymentMethod.trim();
      if (params.expenseId) {
        await updateExpense(params.expenseId, {
          categoryId: params.categoryId,
          date,
          amount,
          memo,
          paymentMethod: trimmedPaymentMethod || undefined,
        });
      } else {
        await addExpense({
          categoryId: params.categoryId,
          date,
          amount,
          memo,
          paymentMethod: trimmedPaymentMethod || undefined,
        });
      }
      push(`/category/${params.categoryId}/day/${date}`);
    } catch (err) {
      console.error(err);
      errorMessage = '保存に失敗しました。';
    } finally {
      saving = false;
    }
  }

  function handleCancel() {
    push(backPath());
  }
</script>

<Header
  title={isEditMode ? '支出を編集' : '支出を追加'}
  showBack={true}
  backTo={backPath()}
/>

<main class="page">
  {#if errorMessage}
    <p class="error-text">{errorMessage}</p>
  {/if}

  {#if loading}
    <p class="muted-text">読み込み中...</p>
  {:else}
    <form class="expense-form" onsubmit={handleSubmit}>
      <label class="field">
        <span class="field__label">カテゴリ</span>
        <input class="field__input" type="text" value={categoryName} disabled />
      </label>

      <label class="field">
        <span class="field__label">日付</span>
        <input class="field__input" type="date" bind:value={date} required />
      </label>

      <label class="field">
        <span class="field__label">金額</span>
        <input
          class="field__input"
          type="number"
          inputmode="numeric"
          min="1"
          step="1"
          placeholder="0"
          bind:value={amountText}
          required
        />
      </label>

      <label class="field">
        <span class="field__label">メモ</span>
        <input class="field__input" type="text" placeholder="メモ（任意）" bind:value={memo} />
      </label>

      <label class="field">
        <span class="field__label">支払方法</span>
        <input
          class="field__input"
          type="text"
          placeholder="支払方法（任意）"
          bind:value={paymentMethod}
        />
      </label>

      <div class="form-actions">
        <button type="button" class="button button--secondary" onclick={handleCancel}>
          キャンセル
        </button>
        <button type="submit" class="button button--primary" disabled={saving}>
          {saving ? '保存中...' : '保存'}
        </button>
      </div>
    </form>
  {/if}
</main>

<style>
  .page {
    padding: 1rem;
    max-width: 480px;
    margin: 0 auto;
  }

  .error-text {
    color: var(--color-danger);
  }

  .muted-text {
    color: var(--color-text-muted);
  }

  .expense-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .field__label {
    font-size: 0.85rem;
    color: var(--color-text-muted);
  }

  .field__input {
    padding: 0.6rem 0.75rem;
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border);
    background-color: var(--color-bg);
    color: var(--color-text);
    font-size: 1rem;
  }

  .field__input:disabled {
    color: var(--color-text-muted);
    background-color: var(--color-bg-elevated);
  }

  .field__input:focus-visible {
    outline: none;
    border-color: var(--color-primary);
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
    margin-top: 0.5rem;
  }

  .button {
    padding: 0.6rem 1.1rem;
    border-radius: var(--radius-md);
    font-size: 0.95rem;
    cursor: pointer;
  }

  .button--primary {
    border: none;
    background-color: var(--color-primary);
    color: var(--color-primary-text);
  }

  .button--primary:disabled {
    opacity: 0.6;
    cursor: default;
  }

  .button--secondary {
    border: 1px solid var(--color-border);
    background-color: var(--color-bg);
    color: var(--color-text);
  }

  .button--secondary:hover,
  .button--secondary:focus-visible {
    border-color: var(--color-primary);
  }
</style>
